import { useState } from 'react'
import { AppBar, Avatar, Box, Divider, Drawer, FormControl, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Select, Skeleton, Stack, ToggleButton, ToggleButtonGroup, Toolbar, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import Chip from '@mui/material/Chip'
import DashboardRounded from '@mui/icons-material/DashboardRounded'
import LanguageRounded from '@mui/icons-material/LanguageRounded'
import AlternateEmailRounded from '@mui/icons-material/AlternateEmailRounded'
import ForwardToInboxRounded from '@mui/icons-material/ForwardToInboxRounded'
import InboxRounded from '@mui/icons-material/InboxRounded'
import SendRounded from '@mui/icons-material/SendRounded'
import CampaignRounded from '@mui/icons-material/CampaignRounded'
import DescriptionRounded from '@mui/icons-material/DescriptionRounded'
import SettingsRounded from '@mui/icons-material/SettingsRounded'
import HistoryRounded from '@mui/icons-material/HistoryRounded'
import CorporateFareRounded from '@mui/icons-material/CorporateFareRounded'
import SellRounded from '@mui/icons-material/SellRounded'
import CategoryRounded from '@mui/icons-material/CategoryRounded'
import GroupRounded from '@mui/icons-material/GroupRounded'
import AdminPanelSettingsRounded from '@mui/icons-material/AdminPanelSettingsRounded'
import KeyRounded from '@mui/icons-material/KeyRounded'
import BackupRounded from '@mui/icons-material/BackupRounded'
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded'
import InsightsRounded from '@mui/icons-material/InsightsRounded'
import MenuRounded from '@mui/icons-material/MenuRounded'
import DarkModeRounded from '@mui/icons-material/DarkModeRounded'
import LightModeRounded from '@mui/icons-material/LightModeRounded'
import LogoutRounded from '@mui/icons-material/LogoutRounded'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { TenantProvider, useTenant } from '../tenant/TenantProvider'
import { useColorMode } from '../theme/AppTheme'
import { AppFooter } from '../components/AppFooter'
import { isLocalDevHost, isManagementConsole } from '../lib/console'

const drawerWidth = 264

export type ConsoleArea = 'platform' | 'organization'

interface NavItem { label: string; path: string; icon: React.ReactNode }

// Cadastros da organização: seguem o seletor de organização do topo. Base que todo mundo vê.
const orgBaseNav: NavItem[] = [
  { label: 'Visão geral', path: '/organizacao/visao-geral', icon: <DashboardRounded /> },
  { label: 'Domínios', path: '/organizacao/dominios', icon: <LanguageRounded /> },
  { label: 'Caixas postais', path: '/organizacao/caixas-postais', icon: <AlternateEmailRounded /> },
  { label: 'Aliases', path: '/organizacao/aliases', icon: <ForwardToInboxRounded /> },
  { label: 'Backups', path: '/organizacao/backups', icon: <BackupRounded /> },
]
// Conteúdo do tenant -- só no console do cliente (o admin da plataforma leva 403 em
// `tenantAccess.canAccessContent`, então nem oferecemos o link).
const orgContentNav: NavItem[] = [
  { label: 'Caixa de entrada', path: '/organizacao/caixa-de-entrada', icon: <InboxRounded /> },
  { label: 'Mensagens', path: '/organizacao/mensagens', icon: <SendRounded /> },
  { label: 'Campanhas', path: '/organizacao/campanhas', icon: <CampaignRounded /> },
  { label: 'Modelos', path: '/organizacao/modelos', icon: <DescriptionRounded /> },
  { label: 'Atividade', path: '/organizacao/atividade', icon: <HistoryRounded /> },
]
// Gestão por organização -- só o console de gestão (admin) enxerga.
const orgManageNav: NavItem[] = [
  { label: 'Usuários', path: '/organizacao/usuarios', icon: <GroupRounded /> },
  { label: 'API Keys', path: '/organizacao/api-keys', icon: <KeyRounded /> },
  { label: 'Atividade', path: '/organizacao/atividade', icon: <HistoryRounded /> },
  { label: 'Lixeira', path: '/organizacao/lixeira', icon: <DeleteOutlineRounded /> },
  { label: 'Configurações da organização', path: '/organizacao/configuracoes', icon: <SettingsRounded /> },
]

function ShellContent({ area }: { area: ConsoleArea }) {
  const theme = useTheme(); const desktop = useMediaQuery(theme.breakpoints.up('md'))
  const [mobileOpen, setMobileOpen] = useState(false); const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const location = useLocation(); const navigate = useNavigate(); const auth = useAuth(); const tenant = useTenant(); const colorMode = useColorMode()
  const userName = auth.profile.name || auth.profile.email || 'Usuário'
  const managementConsole = isManagementConsole()
  // Cadastros genéricos da plataforma -- não dependem de organização nenhuma.
  const canManagePlatform = (auth.isAdmin || auth.isAdminReadOnly) && managementConsole
  // Conteúdo do tenant no console do cliente (ou local, pra testar os dois papéis na mesma máquina).
  const showOrgContent = !auth.isAdmin && !auth.isAdminReadOnly && (!managementConsole || isLocalDevHost())
  // Alternador Plataforma/Organização -- só o admin da plataforma tem as duas áreas.
  const showSwitcher = canManagePlatform

  // A área Plataforma é só do console de gestão. A revenda administra as próprias
  // organizações por `/organizacao/organizacoes`, não pela área Plataforma.
  if (area === 'platform' && !canManagePlatform) return <Navigate to="/organizacao/visao-geral" replace />

  const platformNav: NavItem[] = [
    { label: 'Visão geral', path: '/plataforma/visao-geral', icon: <DashboardRounded /> },
    { label: 'Métricas', path: '/plataforma/metricas', icon: <InsightsRounded /> },
    { label: 'Organizações', path: '/plataforma/organizacoes', icon: <CorporateFareRounded /> },
    { label: 'Planos', path: '/plataforma/planos', icon: <SellRounded /> },
    { label: 'Perfis de atividade', path: '/plataforma/perfis-atividade', icon: <CategoryRounded /> },
    ...(auth.isSuperAdmin ? [{ label: 'Administradores da plataforma', path: '/plataforma/administradores', icon: <AdminPanelSettingsRounded /> }] : []),
    { label: 'Atividade da plataforma', path: '/plataforma/atividade', icon: <HistoryRounded /> },
    { label: 'Lixeira da plataforma', path: '/plataforma/lixeira', icon: <DeleteOutlineRounded /> },
    { label: 'Configurações da plataforma', path: '/plataforma/configuracoes', icon: <SettingsRounded /> },
  ]

  const orgNav: NavItem[] = [
    ...orgBaseNav,
    ...(auth.isReseller ? [{ label: 'Organizações', path: '/organizacao/organizacoes', icon: <CorporateFareRounded /> }] : []),
    ...(showOrgContent ? orgContentNav : []),
    ...(canManagePlatform ? orgManageNav : []),
  ]

  const nav = area === 'platform' ? platformNav : orgNav

  const switchArea = (next: ConsoleArea) => {
    if (next === area) return
    navigate(next === 'platform' ? platformNav[0].path : '/organizacao/visao-geral')
    setMobileOpen(false)
  }

  const drawer = <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
    <Stack direction="row" alignItems="center" spacing={1.3} px={1} py={1.3} mb={2}>
      {tenant.ownTenant?.logoDataUri ? (
        <Avatar src={tenant.ownTenant.logoDataUri} variant="rounded" sx={{ width: 56, height: 56 }} />
      ) : (
        <Box sx={{ width: 56, height: 56, borderRadius: 3, display: 'grid', placeItems: 'center', color: 'white', background: 'linear-gradient(135deg,#4f46e5,#06b6d4)', flexShrink: 0 }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
            <ellipse cx="12" cy="12" rx="9" ry="3.6" transform="rotate(-25 12 12)" stroke="currentColor" strokeWidth="1.6" />
            <circle cx="12" cy="12" r="3" fill="currentColor" />
          </svg>
        </Box>
      )}
      <Box minWidth={0}>
        <Typography fontWeight={800} lineHeight={1.1} noWrap>{tenant.ownTenant?.name ?? 'Órbita'}</Typography>
        <Typography variant="caption" color="text.secondary">{auth.isReseller ? 'Painel de revenda' : 'Control center'}</Typography>
      </Box>
      {auth.isReseller && <Chip label="Agência" size="small" color="primary" variant="outlined" sx={{ ml: 'auto', flexShrink: 0 }} />}
    </Stack>
    {showSwitcher && (
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={area}
        onChange={(_, next: ConsoleArea | null) => next && switchArea(next)}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="platform" sx={{ textTransform: 'none', fontWeight: 600 }}>Plataforma</ToggleButton>
        <ToggleButton value="organization" sx={{ textTransform: 'none', fontWeight: 600 }}>Organização</ToggleButton>
      </ToggleButtonGroup>
    )}
    <List sx={{ display: 'grid', gap: 0.5 }}>{nav.map((item) => <ListItemButton key={item.path} selected={location.pathname === item.path} onClick={() => { navigate(item.path); setMobileOpen(false) }} sx={{ borderRadius: 2.5, py: 1.1 }}><ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon><ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 600 }} /></ListItemButton>)}</List>
  </Box>

  return <Box sx={{ display: 'flex', minHeight: '100vh' }}>
    {desktop ? <Drawer variant="permanent" sx={{ width: drawerWidth, '& .MuiDrawer-paper': { width: drawerWidth, borderRightColor: 'divider', bgcolor: 'background.paper' } }}>{drawer}</Drawer> : <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)} sx={{ '& .MuiDrawer-paper': { width: drawerWidth } }}>{drawer}</Drawer>}
    <Box component="main" sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <AppBar position="sticky" elevation={0}><Toolbar sx={{ gap: 1.5 }}>
        {!desktop && <IconButton onClick={() => setMobileOpen(true)}><MenuRounded /></IconButton>}
        {area === 'organization'
          ? <FormControl size="small" sx={{ minWidth: { xs: 150, sm: 230 } }}>{tenant.loading ? <Skeleton height={40} /> : <Select value={tenant.tenantId ?? ''} displayEmpty onChange={(event) => tenant.setTenantId(event.target.value)} renderValue={(value) => tenant.tenants.find((item) => item.id === value)?.name ?? 'Selecione uma organização'}>{tenant.tenants.map((item) => <MenuItem value={item.id} key={item.id}>{item.name}</MenuItem>)}</Select>}</FormControl>
          : <Typography fontWeight={700} color="text.secondary">Plataforma</Typography>}
        <Box flex={1} />
        <Tooltip title={colorMode.dark ? 'Tema claro' : 'Tema escuro'}><IconButton onClick={colorMode.toggle}>{colorMode.dark ? <LightModeRounded /> : <DarkModeRounded />}</IconButton></Tooltip>
        <IconButton onClick={(event) => setAnchor(event.currentTarget)}><Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: 14, fontWeight: 700 }}>{userName.slice(0, 2).toUpperCase()}</Avatar></IconButton>
        <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}><Box px={2} py={1}><Typography fontWeight={700}>{userName}</Typography><Typography variant="caption" color="text.secondary">{auth.profile.email}</Typography></Box><Divider /><MenuItem onClick={() => auth.logout()}><LogoutRounded fontSize="small" sx={{ mr: 1.5 }} />Sair</MenuItem></Menu>
      </Toolbar></AppBar>
      <Box sx={{ flex: 1, p: { xs: 2, sm: 3, lg: 4 }, maxWidth: 1500, mx: 'auto', width: '100%' }}><Outlet /></Box>
      <AppFooter />
    </Box>
  </Box>
}

export function AppShell({ area }: { area: ConsoleArea }) { return <TenantProvider><ShellContent area={area} /></TenantProvider> }
